import { useState } from "react";
import { ChevronDown, Info } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { OverviewSettingRow } from "@/components/agent/overview/OverviewSettingRow";
import { EnhancedVoiceSelectorV2 } from "@/components/voice/EnhancedVoiceSelectorV2";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { usePreselectedVoices } from "@/hooks/voice/usePreselectedVoices";
import { useVoice } from "@/hooks/voice/useVoices";
import { Agent } from "@/types/agent/agent";
import { LANGUAGES } from "@/utils/config/elevenlabs";

// The only model in this dropdown that is actually English-only on ElevenLabs' end
// (per ElevenLabs docs, Sep 2026). The multilingual list mirrors the models that
// LANGUAGE_PRESETS covers server-side, so every option can carry language presets.
const ENGLISH_ONLY_MODEL = "eleven_flash_v2";
const DEFAULT_MULTILINGUAL_MODEL = "eleven_multilingual_v2";
const MULTILINGUAL_MODELS = ["eleven_flash_v2_5", DEFAULT_MULTILINGUAL_MODEL, "eleven_v3_conversational"];

// Keeps the model in step with the chosen mode, so the select never holds a value
// it can't display (a legacy or other-mode model stored on the genie).
const resolveModelId = (modelId: string | undefined, englishOnly: boolean) => {
  if (englishOnly) return ENGLISH_ONLY_MODEL;
  return modelId && MULTILINGUAL_MODELS.includes(modelId) ? modelId : DEFAULT_MULTILINGUAL_MODEL;
};

const getClosestPreset = (stability: number, similarity: number) => {
  return VOICE_PRESETS.reduce((best, preset) => {
    const d = Math.hypot(preset.stability - stability, preset.similarity - similarity);
    const bestD = Math.hypot(best.stability - stability, best.similarity - similarity);
    return d < bestD ? preset : best;
  });
};

const VOICE_PRESETS = [
  {
    label: "Consistent & Calm",
    description: "Steady, predictable delivery — great for support or info agents",
    useCases: ["Customer support", "FAQ bots", "Helpdesk agents"],
    stability: 0.85,
    similarity: 0.75,
  },
  {
    label: "Warm & Friendly",
    description: "Slight natural variation, feels approachable",
    useCases: ["Onboarding flows", "Appointment reminders", "Friendly check-ins"],
    stability: 0.65,
    similarity: 0.8,
  },
  {
    label: "Expressive & Engaging",
    description: "Dynamic tone, suitable for sales or storytelling",
    useCases: ["Sales outreach", "Product demos", "Interactive experiences"],
    stability: 0.45,
    similarity: 0.85,
  },
  {
    label: "Professional & Neutral",
    description: "Balanced, business-like delivery with high voice fidelity",
    useCases: ["Corporate communications", "Financial services", "HR assistants"],
    stability: 0.75,
    similarity: 0.9,
  },
  {
    label: "Energetic & Lively",
    description: "High expressiveness, works well for enthusiastic or promo use cases",
    useCases: ["Event promotions", "Brand campaigns", "Fitness & coaching"],
    stability: 0.35,
    similarity: 0.75,
  },
  {
    label: "Precise & Clear",
    description: "Maximum stability, minimal variation — ideal for instructions or legal",
    useCases: ["Custom pronunciation rules", "Legal & compliance", "Step-by-step guides"],
    stability: 0.95,
    similarity: 0.7,
  },
];

interface VoiceSpeechCardProps {
  agent: Agent;
  brand?: string;
  isAdmin?: boolean;
}

export const VoiceSpeechCard = ({ agent, brand = "#4E9CFF", isAdmin = false }: VoiceSpeechCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [voiceDetailsOpen, setVoiceDetailsOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const tts = agent.settings?.conversation_config?.tts;
  const agentConfig = agent.settings?.conversation_config?.agent;

  const languagePresetsRaw = (agent.settings?.conversation_config as any)?.language_presets;
  const languagePresets: string[] = languagePresetsRaw
    ? Array.isArray(languagePresetsRaw)
      ? languagePresetsRaw
      : Object.keys(languagePresetsRaw)
    : [];
  const isMultilingual = languagePresets.length > 0;

  const [voiceId, setVoiceId] = useState(tts?.voice_id ?? "");
  const [language, setLanguage] = useState(agentConfig?.language ?? "en");
  const [stability, setStability] = useState(tts?.stability ?? "");
  const [speed, setSpeed] = useState(tts?.speed ?? "");
  const [similarity, setSimilarity] = useState(tts?.similarity_boost ?? "");
  const [modelId, setModelId] = useState(resolveModelId(tts?.model_id, !isMultilingual));
  const [englishOnly, setEnglishOnly] = useState(!isMultilingual);

  const handleEnglishOnlyToggle = (next: boolean) => {
    setEnglishOnly(next);
    // Keep the admin-visible model select in sync so it can't send a model that
    // contradicts the chosen mode (e.g. a multilingual model while English-only is
    // on, which would collide with what isMultilingual tells ElevenLabs).
    setModelId((current) => resolveModelId(current, next));
  };

  const updateAgent = useUpdateAgent();
  const { voices: selectableVoices, isLoading: voicesLoading } = usePreselectedVoices();
  const { data: fallbackVoice } = useVoice(tts?.voice_id);
  const currentVoice = selectableVoices.find((v) => v.id === tts?.voice_id) ?? fallbackVoice ?? undefined;

  const handleEdit = () => {
    setVoiceId(tts?.voice_id ?? "");
    setLanguage(agentConfig?.language ?? "en");
    setStability(tts?.stability ?? "");
    setSpeed(tts?.speed ?? "");
    setSimilarity(tts?.similarity_boost ?? "");
    setModelId(resolveModelId(tts?.model_id, !isMultilingual));
    setEnglishOnly(!isMultilingual);
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        isMultilingual: !englishOnly,
        settings: {
          ...agent.settings,
          conversation_config: {
            ...agent.settings?.conversation_config,
            tts: {
              ...tts,
              voice_id: voiceId,
              stability: stability !== "" ? Number(stability) : undefined,
              speed: speed !== "" ? Number(speed) : undefined,
              similarity_boost: similarity !== "" ? Number(similarity) : undefined,
              model_id: isAdmin ? modelId : tts?.model_id,
            },
            agent: {
              ...agentConfig,
              language,
            },
          },
        },
      },
    });
    toast.success("Voice settings saved");
    setIsEditing(false);
  };

  const currentLanguage = LANGUAGES.find((l) => l.code === (agentConfig?.language ?? "en"));

  return (
    <OverviewSectionCard
      title="Voice & Speech"
      subtitle="Speech characteristics and language"
      genieIcon="microphone"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Voice</label>
            <EnhancedVoiceSelectorV2
              value={voiceId}
              onChange={setVoiceId}
              voices={selectableVoices}
              voicesLoading={voicesLoading}
              currentVoice={selectableVoices.find((v) => v.id === voiceId)}
              restrictToVoices
              hidePlayButton
            />
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                Advanced: enter voice ID manually
              </summary>
              <input
                type="text"
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="w-full mt-2 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Voice ID"
              />
            </details>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5">
            <div>
              <label className="block text-sm font-medium text-gray-700">English only</label>
              <p className="text-xs text-gray-500 mt-0.5">
                Turns off mid-call language switching so the genie always speaks English.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={englishOnly}
              onClick={() => handleEnglishOnlyToggle(!englishOnly)}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                englishOnly ? "bg-[#4E9CFF]" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  englishOnly ? "translate-x-[18px]" : "translate-x-[3px]"
                }`}
              />
            </button>
          </div>
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TTS Model ID</label>
              <select
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                disabled={englishOnly}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary disabled:bg-gray-100 disabled:text-gray-500"
              >
                {(englishOnly ? [ENGLISH_ONLY_MODEL] : MULTILINGUAL_MODELS).map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
              {englishOnly && (
                <p className="text-xs text-gray-500 mt-1">Locked to eleven_flash_v2 while English only is on.</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Voice Style</label>
            <div className="grid grid-cols-2 gap-2">
              {VOICE_PRESETS.map((preset) => {
                const isSelected = Number(stability) === preset.stability && Number(similarity) === preset.similarity;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => {
                      setStability(String(preset.stability));
                      setSimilarity(String(preset.similarity));
                    }}
                    className={`text-left px-3 py-2.5 rounded-xl border text-sm transition-all ${
                      isSelected
                        ? "border-[#4E9CFF] bg-[#4E9CFF]/8 text-gray-900"
                        : "border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`block font-medium text-xs mb-0.5 ${isSelected ? "text-[#4E9CFF]" : "text-gray-800"}`}
                    >
                      {preset.label}
                    </span>
                    <span className="block text-xs text-gray-500 leading-snug">{preset.description}</span>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {preset.useCases.map((uc) => (
                        <span
                          key={uc}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                            isSelected
                              ? "border-[#4E9CFF]/30 bg-[#4E9CFF]/8 text-[#4E9CFF]"
                              : "border-gray-200 bg-gray-50 text-gray-500"
                          }`}
                        >
                          {uc}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
              Advanced settings
            </button>
            {advancedOpen && (
              <div className="mt-3 grid grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-sm font-medium text-gray-700">Stability</label>
                    <div className="relative group">
                      <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full left-0 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 leading-snug opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                        Controls how much the voice varies between sentences. High = steady and predictable. Low =
                        expressive and emotional.
                        <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={stability}
                    onChange={(e) => setStability(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Speed</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="1.2"
                    value={speed}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setSpeed(isNaN(val) ? "" : Math.min(val, 1.2));
                    }}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-sm font-medium text-gray-700">Similarity</label>
                    <div className="relative group">
                      <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                      <div className="absolute bottom-full right-0 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 leading-snug opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                        Controls how closely the output matches the original voice sample. High = more faithful but can
                        amplify artifacts. Lower = smoother but less distinctive.
                        <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-900" />
                      </div>
                    </div>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={similarity}
                    onChange={(e) => setSimilarity(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OverviewSettingRow
            label="Voice"
            value={
              <span className="inline-flex items-center gap-1 font-medium">
                {agent.voice_label || "Default Voice"}
                {currentVoice && (currentVoice.description || currentVoice.labels) && (
                  <button
                    type="button"
                    onClick={() => setVoiceDetailsOpen((o) => !o)}
                    className="text-gray-400 text-[10px] hover:text-gray-600 leading-none transition-transform"
                    style={{ transform: voiceDetailsOpen ? "rotate(180deg)" : undefined }}
                  >
                    ▼
                  </button>
                )}
              </span>
            }
          />
          {voiceDetailsOpen && currentVoice && (
            <div className="-mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              {currentVoice.description && (
                <p className="text-xs text-gray-600 leading-relaxed">{currentVoice.description}</p>
              )}
              {currentVoice.labels && (
                <div className="flex flex-wrap gap-1">
                  {currentVoice.labels.accent && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {currentVoice.labels.accent}
                    </span>
                  )}
                  {currentVoice.labels.age && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      {currentVoice.labels.age}
                    </span>
                  )}
                  {currentVoice.labels.gender && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      {currentVoice.labels.gender}
                    </span>
                  )}
                  {currentVoice.labels.use_case && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      {currentVoice.labels.use_case}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <OverviewSettingRow
            label="Primary Language"
            value={
              <span className="font-medium">
                {currentLanguage ? currentLanguage.name : (agentConfig?.language?.toUpperCase() ?? "EN")}
              </span>
            }
          />
          <OverviewSettingRow
            label="Languages"
            value={<span className="font-medium">{isMultilingual ? "Multilingual" : "English only"}</span>}
          />
          {(tts?.speed != null || tts?.stability != null || tts?.similarity_boost != null) && (
            <div className="space-y-3">
              {tts?.speed != null && (
                <OverviewSettingRow
                  label="Speech Speed"
                  value={
                    <span className="font-medium">
                      {tts.speed}x{tts.speed < 0.8 ? " (Slower)" : tts.speed > 1.2 ? " (Faster)" : " (Normal)"}
                    </span>
                  }
                />
              )}
              {(tts?.stability != null || tts?.similarity_boost != null) &&
                (() => {
                  const closestPreset =
                    tts?.stability != null && tts?.similarity_boost != null
                      ? getClosestPreset(tts.stability, tts.similarity_boost)
                      : undefined;
                  return (
                    <div className="space-y-2">
                      {closestPreset ? (
                        <>
                          <OverviewSettingRow
                            label="Voice Style"
                            value={
                              <div className="text-right">
                                <span className="text-sm font-semibold text-gray-900">{closestPreset.label}</span>
                                <p className="text-xs text-gray-500 mt-0.5">{closestPreset.description}</p>
                              </div>
                            }
                          />
                          {isAdmin && tts?.model_id && (
                            <OverviewSettingRow
                              label="TTS Model ID"
                              value={<span className="font-medium font-mono text-xs">{tts.model_id}</span>}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {tts?.stability != null && (
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-700 w-32 shrink-0">Stability</span>
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${tts.stability * 100}%`, backgroundColor: brand }}
                                />
                              </div>
                              <span className="font-medium text-xs w-14 shrink-0 text-right">
                                {Math.round(tts.stability * 100)}%
                              </span>
                            </div>
                          )}
                          {tts?.similarity_boost != null && (
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-700 w-32 shrink-0">Similarity</span>
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${tts.similarity_boost * 100}%`, backgroundColor: brand }}
                                />
                              </div>
                              <span className="font-medium text-xs w-14 shrink-0 text-right">
                                {Math.round(tts.similarity_boost * 100)}%
                              </span>
                            </div>
                          )}
                          {isAdmin && tts?.model_id && (
                            <OverviewSettingRow
                              label="TTS Model ID"
                              value={<span className="font-medium font-mono text-xs">{tts.model_id}</span>}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}
            </div>
          )}
          {languagePresets.length > 0 && (
            <OverviewSettingRow
              label="Genie Can Speak"
              fullWidth
              value={
                <div className="flex flex-wrap gap-1.5">
                  {languagePresets.map((code) => {
                    const lang = LANGUAGES.find((l) => l.code === code);
                    return (
                      <span
                        key={code}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-gray-200 text-gray-900 bg-gray-50"
                      >
                        {lang ? `${lang.flag} ${lang.name}` : code}
                      </span>
                    );
                  })}
                </div>
              }
            />
          )}
        </div>
      )}
    </OverviewSectionCard>
  );
};
